import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ROUTE_DEFINITIONS } from '@/routes';
import type { TableColumn } from '@/components/ui/tables/Table';
import type { ExpandableConfig } from '@/components/ui/tables/Table.types';
import { AuditExpandedRow } from '@/components/features/audit/AuditExpandedRow';
import { auditService } from '@/api/domains/audit/audit.service';
import { mapAuditFilterOptionToSelectOption } from '@/api/domains/audit/audit.mapper';
import type { AuditFilterOption, AuditRecord } from '@/api/domains/audit/types';
import { useUI } from '@/hooks/ui/useUI';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { formatDateToYYYYMMDD } from '@/utils';
import type { UIExportFormat } from '@/types/fileExport.types';
import { useAuditExport } from './useAuditExport';


// ── Types ──────────────────────────────────────────────────────────────────

export interface AuditPendingFilters {
  startDate: string | null;
  endDate: string | null;
  entityId: string | null;
  programId: string | null;
  actionId: string | null;
  userId: string | null;
}

type AuditAppliedFilters = AuditPendingFilters;

export interface UseAuditListResult {
  rows: AuditRecord[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  loading: boolean;
  isSearching: boolean;
  canSearch: boolean;
  columns: TableColumn<AuditRecord>[];
  expandable: ExpandableConfig<AuditRecord>;
  pendingFilters: AuditPendingFilters;
  setPendingFilters: React.Dispatch<React.SetStateAction<AuditPendingFilters>>;
  handleSearch: () => void;
  onSearch: (term: string) => void;
  onSortChange: (key: string, direction: 'asc' | 'desc') => void;
  entitiesOptions: Array<{ value: string; label: string }>;
  programsOptions: Array<{ value: string; label: string }>;
  actionsOptions: Array<{ value: string; label: string }>;
  usersOptions: Array<{ value: string; label: string }>;
  combosLoaded: boolean;
  isExporting: boolean;
  onExport: (format: UIExportFormat) => Promise<void>;
}

// ── Initial filter state ───────────────────────────────────────────────────

// Extracted from `buildInitialFilters` so hydration can refresh just the date window (see
// `onHydrated` below) without touching the rest of the persisted filters.
const buildDefaultDateRange = (): Pick<AuditPendingFilters, 'startDate' | 'endDate'> => {
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);
  return {
    startDate: formatDateToYYYYMMDD(thirtyDaysAgo),
    endDate: formatDateToYYYYMMDD(today),
  };
};

const buildInitialFilters = (): AuditPendingFilters => ({
  ...buildDefaultDateRange(),
  entityId: null,
  programId: null,
  actionId: null,
  userId: null,
});

// ── Hook ───────────────────────────────────────────────────────────────────

export function useAuditList(): UseAuditListResult {
  const { t: translate } = useTranslation();
  const { showApiError, showNotification } = useUI();
  const navigate = useNavigate();
  const { canView, isLoaded: permissionsLoaded } = usePagePermissions();

  // ── Permission guard ──────────────────────────────────────────────────
  useEffect(() => {
    if (permissionsLoaded && !canView) {
      void navigate(ROUTE_DEFINITIONS.DASHBOARD_ASISTENCIA.path);
    }
  }, [permissionsLoaded, canView, navigate]);

  // ── Pending filters (what user is editing; not yet applied) ───────────
  const [pendingFilters, setPendingFilters] = useState<AuditPendingFilters>(buildInitialFilters);

  // ── Search button loading state ────────────────────────────────────────
  const [isSearching, setIsSearching] = useState(false);

  // ── Fetch function delegated to usePaginatedList ──────────────────────
  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<AuditAppliedFilters>,
      _signal: AbortSignal,
    ): Promise<PaginatedFetchResult<AuditRecord> | null> => {
      const response = await auditService.getRecords({
        page: params.page,
        pageSize: params.pageSize,
        searchText: params.searchText ?? null,
        orderBy: params.sort?.key ?? null,
        orderDirection: params.sort?.direction ?? null,
        dateFrom: params.filters.startDate,
        dateTo: params.filters.endDate,
        objectName: params.filters.entityId,
        program: params.filters.programId,
        action: params.filters.actionId,
        user: params.filters.userId,
      });

      if (!response.success || !response.data) {
        showApiError(response, translate('audit:messages.loadError'));
        return null;
      }

      return {
        items: response.data.data,
        totalRecords: response.data.totalRecords,
      };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    setFilters: setAppliedFilters,
    debouncedSearchText,
    filters,
    sort,
  } = usePaginatedList<AuditRecord, AuditAppliedFilters>({
    fetchFn,
    initialFilters: buildInitialFilters(),
    initialSort: { key: 'date', direction: 'DESC' },
    enabled: permissionsLoaded && canView,
    screenCode: ROUTE_DEFINITIONS.AUDITORIA.code,
    // The panel lives in `pendingFilters` (below), separate from `usePaginatedList`'s own
    // `filters` — sync it with whatever preference just got restored. The date range is
    // recomputed fresh instead of restored as-is: `startDate`/`endDate` are a relative "last 30
    // days" window, so persisting them as absolute values would freeze that window at whenever
    // the user last left the screen instead of rolling forward.
    onHydrated: (hydrated) => {
      const restored = { ...hydrated.filters, ...buildDefaultDateRange() };
      setPendingFilters(restored);
      return restored;
    },
  });

  // ── Reset isSearching when the fetch cycle completes ───────────────────
  useEffect(() => {
    if (!loading) {
      setIsSearching(false);
    }
  }, [loading]);

  // ── Export ─────────────────────────────────────────────────────────────
  const { isExporting, onExport } = useAuditExport({
    searchText: debouncedSearchText,
    filters,
    sort,
  });

  // ── Combos ────────────────────────────────────────────────────────────
  const entitiesCatalog = useCatalogLoader<AuditFilterOption>(
    () => auditService.getEntities(),
    mapAuditFilterOptionToSelectOption,
    [],
    false,
    () => { showNotification('error', translate('audit:messages.loadFiltersError')); },
  );

  const programsCatalog = useCatalogLoader<AuditFilterOption>(
    () => auditService.getPrograms(),
    mapAuditFilterOptionToSelectOption,
    [],
    false,
    () => { showNotification('error', translate('audit:messages.loadFiltersError')); },
  );

  const actionsCatalog = useCatalogLoader<AuditFilterOption>(
    () => auditService.getActions(),
    mapAuditFilterOptionToSelectOption,
    [],
    false,
    () => { showNotification('error', translate('audit:messages.loadFiltersError')); },
  );

  const usersCatalog = useCatalogLoader<AuditFilterOption>(
    () => auditService.getUsers(),
    mapAuditFilterOptionToSelectOption,
    [],
    false,
    () => { showNotification('error', translate('audit:messages.loadFiltersError')); },
  );

  // ── Handlers ───────────────────────────────────────────────────────────

  const canSearch = useMemo(
    () => pendingFilters.startDate !== null && pendingFilters.endDate !== null,
    [pendingFilters.startDate, pendingFilters.endDate],
  );

  const handleSearch = useCallback(() => {
    setIsSearching(true);
    // Spread to always create a new object reference, ensuring a fetch is
    // triggered even when filter values have not changed since the last search.
    setAppliedFilters({ ...pendingFilters });
  }, [pendingFilters, setAppliedFilters]);

  // ── Columns ─────────────────────────────────────────────────────────────

  const columns: TableColumn<AuditRecord>[] = useMemo(() => [
    { key: 'date',    header: translate('audit:table.date'),    sortable: true },
    { key: 'user',    header: translate('audit:table.user'),    sortable: true },
    { key: 'entity',  header: translate('audit:table.entity'),  sortable: true },
    { key: 'action',  header: translate('audit:table.action'),  sortable: true },
    { key: 'program', header: translate('audit:table.program'), sortable: true },
  ], [translate]);

  // ── Expandable rows ──────────────────────────────────────────────────────

  const renderExpanded = useCallback(
    (record: AuditRecord) => <AuditExpandedRow record={record} />,
    [],
  );

  const rowExpandable = useCallback((_record: AuditRecord) => true, []);

  const expandable = useMemo<ExpandableConfig<AuditRecord>>(() => ({
    showExpandColumn: true,
    renderExpanded,
    rowExpandable,
    accordion: true,
  }), [renderExpanded, rowExpandable]);

  // ── Combo options ────────────────────────────────────────────────────────

  const combosLoaded =
    entitiesCatalog.isLoaded &&
    programsCatalog.isLoaded &&
    actionsCatalog.isLoaded &&
    usersCatalog.isLoaded;

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    isSearching,
    canSearch,
    columns,
    expandable,
    pendingFilters,
    setPendingFilters,
    handleSearch,
    onSearch,
    onSortChange,
    entitiesOptions: entitiesCatalog.options,
    programsOptions: programsCatalog.options,
    actionsOptions: actionsCatalog.options,
    usersOptions: usersCatalog.options,
    combosLoaded,
    isExporting,
    onExport,
  };
}

export default useAuditList;

